import * as React from 'react';
import { useMemo } from 'react';

import { translate } from '~l10n/client';

import { IconPencil } from '~shared/components/icons/standard';
import {
  RenameFolderDialogDispatch,
  openRenameFolder,
} from '~teacher/components/workspaces/RenameWorkspaceFolderDialog/useRenameWorkspaceFolderDialog';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';

import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';

interface UseRenameFolderActionParams {
  paywallCallback: () => void;
  renameFolderDialogDispatch: RenameFolderDialogDispatch;
  workspaceId: string;
}

export function useRenameFolderAction({
  paywallCallback,
  renameFolderDialogDispatch,
  workspaceId,
}: UseRenameFolderActionParams) {
  const {
    permissions: { renameWorkspaceFolder: canRenameWorkspaceFolder },
    roleQuery,
  } = useWorkspacePermissionsV2({ workspaceId });
  return useMemo(
    () => ({
      key: 'rename',
      title: translate('Rename'),
      icon: <IconPencil />,
      action: ({ data, close }) => {
        const firstItem = data[0];
        if (firstItem.type !== 'folder') return; // Rename quizzes is not supported. Constraint should already handle this.

        const { workspaceId, id, name } = firstItem;
        renameFolderDialogDispatch(
          openRenameFolder({
            workspaceFolderId: id,
            workspaceId,
            initialWorkspaceFolderName: name,
          })
        );

        close();
      },
      constraint: ({ data }) => {
        if (data.length > 1) return false;
        if (roleQuery.data === WorkspaceRole.Viewer) return false;

        return !data.some((d) => d.type !== 'folder');
      },
      showProBadge: !canRenameWorkspaceFolder,
      disabled: ({ data }) => data.length >= 1 && !canRenameWorkspaceFolder,
      paywallCallback: !canRenameWorkspaceFolder ? paywallCallback : null,
    }),
    [
      canRenameWorkspaceFolder,
      paywallCallback,
      renameFolderDialogDispatch,
      roleQuery.data,
    ]
  );
}
